import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import { enumValueFor } from "@storyboard/dispatch-view/fieldKinds";
import type { DispatchTableRow } from "./dispatchTableRow";

/** One group of Dispatch rows: the raw value it stands for, what to call it, how many rows it holds, and its fold. */
export type DispatchGroup = { key: string; label: string; count: number; collapsed: boolean };

/** The group key of rows with no value for the field. */
export const EMPTY_GROUP_KEY = "";

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Orders group keys the way a dispatcher expects the field's values: enum values in registry order (unknown values
 * after, alphabetically), booleans yes before no, everything else alphabetically. The empty group comes last.
 */
export function orderGroupKeys(keys: readonly string[], kind: FieldKind): string[] {
  const present = keys.filter((key) => key !== EMPTY_GROUP_KEY);
  let ordered: string[];
  if (kind.valueType === "enum" && kind.values) {
    const rank = new Map(kind.values.map((value, index) => [value.value, index]));
    ordered = [...present].sort((a, b) => {
      const ra = rank.get(a);
      const rb = rank.get(b);
      if (ra != null && rb != null) return ra - rb;
      if (ra != null || rb != null) return ra != null ? -1 : 1;
      return compareText(a, b);
    });
  } else if (kind.valueType === "boolean") {
    ordered = [...present].sort((a, b) => (a === b ? 0 : a === "true" ? -1 : 1));
  } else {
    ordered = [...present].sort(compareText);
  }
  return keys.includes(EMPTY_GROUP_KEY) ? [...ordered, EMPTY_GROUP_KEY] : ordered;
}

/**
 * The header text for a group: the value's registry label (an enum value's, or a boolean state's name such as
 * "Acknowledged"), Yes/No for a boolean without names, `No <field>` for the empty group.
 */
export function groupLabel(key: string, kind: FieldKind, fieldLabel: string): string {
  if (key === EMPTY_GROUP_KEY) return `No ${fieldLabel}`;
  return enumValueFor(kind, key)?.label ?? (kind.valueType === "boolean" ? (key === "true" ? "Yes" : "No") : key);
}

/**
 * Groups filtered, sorted rows by the projected value under `key`, keeping each group's rows in the order given.
 *
 * A multi-value field (a tag) files a row under each of its values, so the copies get distinct ids (`<id>@<group>`)
 * and the table, the selection, and the expanded row can tell them apart. Rows of a collapsed group are left out of
 * the returned rows but still counted in their group, which is how the table knows to skip them.
 */
export function groupDispatchRows(
  rows: readonly DispatchTableRow[],
  key: string,
  kind: FieldKind,
  fieldLabel: string,
  collapsed: ReadonlySet<string>,
): { groups: DispatchGroup[]; rows: DispatchTableRow[] } {
  const buckets = new Map<string, DispatchTableRow[]>();
  const appearances = new Map<string, number>();
  for (const row of rows) {
    const raw = row.record[key];
    const values = Array.isArray(raw) ? (raw.length > 0 ? raw : [EMPTY_GROUP_KEY]) : [raw ?? EMPTY_GROUP_KEY];
    const distinct = [...new Set(values)];
    appearances.set(row.id, distinct.length);
    for (const value of distinct) {
      const bucket = buckets.get(value);
      if (bucket) bucket.push(row);
      else buckets.set(value, [row]);
    }
  }

  const groups: DispatchGroup[] = [];
  const grouped: DispatchTableRow[] = [];
  for (const groupKey of orderGroupKeys([...buckets.keys()], kind)) {
    const bucket = buckets.get(groupKey) ?? [];
    const isCollapsed = collapsed.has(groupKey);
    groups.push({
      key: groupKey,
      label: groupLabel(groupKey, kind, fieldLabel),
      count: bucket.length,
      collapsed: isCollapsed,
    });
    if (isCollapsed) continue;
    for (const row of bucket) {
      grouped.push((appearances.get(row.id) ?? 1) > 1 ? { ...row, id: `${row.id}@${groupKey}` } : row);
    }
  }
  return { groups, rows: grouped };
}

/** The collapsed set with one group folded or unfolded. */
export function toggleCollapsedGroup(collapsed: readonly string[], key: string): string[] {
  return collapsed.includes(key) ? collapsed.filter((candidate) => candidate !== key) : [...collapsed, key];
}
